import { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Stethoscope, Store, Briefcase, Scissors } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';

const BUSINESS_TYPES_FR = [
  { type: 'Cabinet médical', icon: 'health' },
  { type: 'Cabinet dentaire', icon: 'health' },
  { type: 'Kinésithérapeute', icon: 'health' },
  { type: 'Ostéopathe', icon: 'health' },
  { type: 'Opticien', icon: 'health' },
  { type: 'Pharmacie', icon: 'health' },
  { type: 'Boutique', icon: 'store' },
  { type: 'Magasin de prêt-à-porter', icon: 'store' },
  { type: 'Salon de coiffure', icon: 'beauty' },
  { type: 'Institut de beauté', icon: 'beauty' },
  { type: 'Garage automobile', icon: 'service' },
  { type: 'Agence immobilière', icon: 'service' },
  { type: 'Cabinet comptable', icon: 'service' },
  { type: 'Restaurant', icon: 'store' },
  { type: 'Boulangerie', icon: 'store' },
  { type: 'Hôtel', icon: 'service' },
  { type: 'Centre de bien-être', icon: 'beauty' },
  { type: 'Clinique vétérinaire', icon: 'health' },
];

const BUSINESS_TYPES_EN = [
  { type: 'Medical Practice', icon: 'health' },
  { type: 'Dental Clinic', icon: 'health' },
  { type: 'Physiotherapist', icon: 'health' },
  { type: 'Osteopath', icon: 'health' },
  { type: 'Optician', icon: 'health' },
  { type: 'Pharmacy', icon: 'health' },
  { type: 'Retail Store', icon: 'store' },
  { type: 'Clothing Store', icon: 'store' },
  { type: 'Hair Salon', icon: 'beauty' },
  { type: 'Beauty Salon', icon: 'beauty' },
  { type: 'Auto Repair Shop', icon: 'service' },
  { type: 'Real Estate Agency', icon: 'service' },
  { type: 'Accounting Firm', icon: 'service' },
  { type: 'Restaurant', icon: 'store' },
  { type: 'Bakery', icon: 'store' },
  { type: 'Hotel', icon: 'service' },
  { type: 'Wellness Center', icon: 'beauty' },
  { type: 'Veterinary Clinic', icon: 'health' },
];

const NOTIFICATION_TEMPLATES_FR = [
  (business: string) => `Un ${business} vient de sécuriser son e-réputation avec OuraTrust !`,
  (business: string) => `Un nouveau ${business} a rejoint le Plan Pro.`,
  (business: string) => `Un ${business} vient d'automatiser ses réponses aux avis Google.`,
  (business: string) => `Un ${business} utilise désormais le Pompier IA.`,
  () => 'Déjà 12 professionnels de santé nous ont rejoint cette semaine.',
  () => '+35 établissements ont boosté leur note Google ce mois-ci.',
  (business: string) => `Un ${business} vient d'améliorer sa note Google.`,
];

const NOTIFICATION_TEMPLATES_EN = [
  (business: string) => `A ${business} just secured their online reputation with OuraTrust!`,
  (business: string) => `A new ${business} joined the Pro Plan.`,
  (business: string) => `A ${business} just automated their Google review responses.`,
  (business: string) => `A ${business} is now using AI Firefighter.`,
  () => 'Already 12 healthcare professionals joined us this week.',
  () => '+35 businesses boosted their Google rating this month.',
  (business: string) => `A ${business} just improved their Google rating.`,
];

const CITIES_FR = ['Paris', 'Lyon', 'Marseille', 'Bordeaux', 'Toulouse', 'Nice', 'Nantes', 'Lille'];
const CITIES_EN = ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Miami', 'Seattle', 'Boston', 'Denver'];
const STORAGE_KEY = 'ouratrust_seen_notifications_v2';

const TIME_DURATIONS_EN = [
  'just now', '2 min ago', '5 min ago', '8 min ago', '12 min ago',
  '18 min ago', '25 min ago', '32 min ago', '45 min ago', '1 hour ago',
];

const TIME_DURATIONS_FR = [
  "à l'instant", 'il y a 2 min', 'il y a 5 min', 'il y a 8 min', 'il y a 12 min',
  'il y a 18 min', 'il y a 25 min', 'il y a 32 min', 'il y a 45 min', 'il y a 1h',
];

interface NotificationData {
  id: string;
  message: string;
  time: string;
  iconType: string;
}

function SocialProofToastClient() {
  const { language } = useLanguage();
  const [currentNotification, setCurrentNotification] = useState<NotificationData | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getSeenNotifications = useCallback((): string[] => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, []);

  const markAsSeen = useCallback((id: string) => {
    try {
      const seen = getSeenNotifications();
      if (!seen.includes(id)) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...seen, id].slice(-30)));
      }
    } catch {
      // no-op
    }
  }, [getSeenNotifications]);

  const generateNotification = useCallback((): NotificationData => {
    const businessTypes = language === 'fr' ? BUSINESS_TYPES_FR : BUSINESS_TYPES_EN;
    const templates = language === 'fr' ? NOTIFICATION_TEMPLATES_FR : NOTIFICATION_TEMPLATES_EN;
    const cities = language === 'fr' ? CITIES_FR : CITIES_EN;
    const timeDurations = language === 'fr' ? TIME_DURATIONS_FR : TIME_DURATIONS_EN;

    const business = businessTypes[Math.floor(Math.random() * businessTypes.length)];
    const template = templates[Math.floor(Math.random() * templates.length)];
    const city = cities[Math.floor(Math.random() * cities.length)];
    const time = timeDurations[Math.floor(Math.random() * timeDurations.length)];

    let message = template(business.type);

    if (Math.random() > 0.5 && !message.includes('cette semaine') && !message.includes('this week')) {
      message = language === 'fr'
        ? message.replace('!', ` à ${city} !`).replace(/\.$/, ` à ${city}.`)
        : message.replace('!', ` in ${city}!`).replace(/\.$/, ` in ${city}.`);
    }

    return {
      id: `${business.type}-${template.toString().substring(0, 20)}-${Date.now()}`,
      message,
      time,
      iconType: business.icon,
    };
  }, [language]);

  const getRandomNotification = useCallback((): NotificationData => {
    const seen = getSeenNotifications();

    for (let i = 0; i < 10; i += 1) {
      const notification = generateNotification();
      if (!seen.includes(notification.id)) {
        return notification;
      }
    }

    window.localStorage.removeItem(STORAGE_KEY);
    return generateNotification();
  }, [generateNotification, getSeenNotifications]);

  const showNotification = useCallback(() => {
    const notification = getRandomNotification();
    setCurrentNotification(notification);
    setIsVisible(true);
    markAsSeen(notification.id);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => {
      setIsVisible(false);
    }, 5000);
  }, [getRandomNotification, markAsSeen]);

  const scheduleNext = useCallback(() => {
    intervalRef.current = window.setTimeout(() => {
      showNotification();
      scheduleNext();
    }, 60000);
  }, [showNotification]);

  useEffect(() => {
    const initialTimeout = window.setTimeout(() => {
      showNotification();
      scheduleNext();
    }, 30000);

    return () => {
      window.clearTimeout(initialTimeout);
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      if (intervalRef.current) window.clearTimeout(intervalRef.current);
    };
  }, [showNotification, scheduleNext]);

  const getIcon = (iconType: string) => {
    switch (iconType) {
      case 'health':
        return <Stethoscope className="h-5 w-5 text-accent" />;
      case 'store':
        return <Store className="h-5 w-5 text-accent" />;
      case 'beauty':
        return <Scissors className="h-5 w-5 text-accent" />;
      case 'service':
        return <Briefcase className="h-5 w-5 text-accent" />;
      default:
        return <CheckCircle className="h-5 w-5 text-accent" />;
    }
  };

  return (
    <AnimatePresence>
      {isVisible && currentNotification ? (
        <motion.div
          initial={{ opacity: 0, x: -100, y: 20 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: -100, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="fixed bottom-6 left-6 z-50 max-w-sm"
        >
          <div className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 shadow-xl shadow-black/10">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-accent/10">
              {getIcon(currentNotification.iconType)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium leading-snug text-foreground">{currentNotification.message}</p>
              <p className="mt-1 text-xs text-muted-foreground">{currentNotification.time} • OuraTrust</p>
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export function SocialProofToast() {
  if (typeof document === 'undefined') {
    return null;
  }

  return <SocialProofToastClient />;
}
